import type { ShopCartLine, ShopCartState } from '../types';

export const CART_STORAGE_KEY = 'a2s_shop_cart_v1';

const EMPTY: ShopCartState = { version: 1, lines: [] };

const isValidLine = (raw: unknown): raw is ShopCartLine => {
  if (!raw || typeof raw !== 'object') return false;
  const line = raw as Partial<ShopCartLine>;
  return (
    typeof line.productId === 'string' &&
    line.productId.length > 0 &&
    typeof line.slug === 'string' &&
    typeof line.name === 'string' &&
    typeof line.unitPrice === 'number' &&
    Number.isFinite(line.unitPrice) &&
    typeof line.qty === 'number' &&
    Number.isInteger(line.qty) &&
    line.qty > 0
  );
};

/**
 * Reads the cart from localStorage. Any corruption yields an empty cart rather
 * than throwing — this runs inside a provider that wraps the entire app, so a
 * parse error here would otherwise white-screen the whole site.
 */
export const loadCart = (): ShopCartState => {
  try {
    const raw = localStorage.getItem(CART_STORAGE_KEY);
    if (!raw) return EMPTY;

    const parsed = JSON.parse(raw) as Partial<ShopCartState>;
    if (!parsed || typeof parsed !== 'object' || !Array.isArray(parsed.lines)) {
      return EMPTY;
    }

    const lines = parsed.lines.filter(isValidLine).map((line) => ({
      ...line,
      image: typeof line.image === 'string' ? line.image : null,
      currency: typeof line.currency === 'string' ? line.currency : 'MAD',
      addedAt: typeof line.addedAt === 'string' ? line.addedAt : new Date().toISOString(),
    }));

    return { version: 1, lines };
  } catch (error) {
    console.warn('[shop] Could not read the saved cart; starting empty.', error);
    return EMPTY;
  }
};

/** Persists the cart. Failures (private mode, quota) are non-fatal. */
export const saveCart = (state: ShopCartState): void => {
  try {
    localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(state));
  } catch (error) {
    console.warn('[shop] Could not save the cart.', error);
  }
};

export const clearStoredCart = (): void => {
  try {
    localStorage.removeItem(CART_STORAGE_KEY);
  } catch {
    /* no-op */
  }
};
